import { predicates } from "../predicates";
import type { ObjectTypeConfig } from "../types";

export const FILE_RECORD: ObjectTypeConfig = {
  actions: [
    {
      id: "open",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
    },
    {
      id: "star",
      surfaces: ["left-rail", "main-view", "context-menu", "card-overlay"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "card-overlay": "corner-star",
      },
      hiddenWhen: predicates.isFavorite,
    },
    {
      id: "unstar",
      surfaces: ["left-rail", "main-view", "context-menu", "card-overlay"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "card-overlay": "corner-star",
      },
      hiddenWhen: { not: predicates.isFavorite },
    },

    {
      id: "share",
      surfaces: [
        "left-rail",
        "main-view",
        "context-menu",
        "floating-toolbar",
        "pane-header",
      ],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
        "pane-header": "secondary-button",
      },
      hiddenWhen: predicates.cannotShare,
    },
    {
      id: "publish",
      surfaces: [
        "left-rail",
        "main-view",
        "context-menu",
        "floating-toolbar",
        "pane-header",
      ],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
        "pane-header": "primary-button",
      },
      hiddenWhen: { not: { state: "showPublish" } },
    },
    {
      // Only offer Copy URL once the file is published — it copies the public
      // share URL, not the internal one. An unpublished file has no useful
      // public link to hand out.
      id: "copy-url",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
      hiddenWhen: { not: { state: "isPublished" } },
    },
    {
      id: "download",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
      variantSource: "file-record-export",
    },
    {
      id: "copy-to-workspace",
      surfaces: ["left-rail", "main-view", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "main-view": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-utility",
      },
    },
    {
      id: "rename",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [predicates.sharedWithMeScope, predicates.favoritesScope],
      },
      disabledWhen: predicates.cannotEdit,
    },
    // Hide / Show on canvas — the non-destructive pair. Hide surfaces on the
    // canvas node menus (context menu + toolbar More) and on explorer rail
    // rows; Show only makes sense from the rail (a hidden node can't be
    // right-clicked on the board).
    {
      id: "remove-from-canvas",
      surfaces: ["left-rail", "context-menu", "floating-toolbar"],
      presentation: {
        "left-rail": "menu-item",
        "context-menu": "menu-item",
        "floating-toolbar": "toolbar-more-submenu",
      },
      hiddenWhen: {
        any: [
          {
            all: [
              { not: { scope: "canvas-explorer" } },
              { not: { state: "onCanvasDetail" } },
            ],
          },
          { state: "hiddenOnCanvas" },
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "show-on-canvas",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          { not: { scope: "canvas-explorer" } },
          { not: { state: "hiddenOnCanvas" } },
        ],
      },
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "delete",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: {
        any: [
          predicates.sharedWithMeScope,
          predicates.favoritesScope,
          { state: "onCanvasDetail" },
        ],
      },
      disabledWhen: predicates.cannotDelete,
    },

    {
      id: "version-history",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: { not: { state: "showVersionHistory" } },
    },
    {
      id: "locate-on-canvas",
      surfaces: ["left-rail"],
      presentation: "menu-item",
      hiddenWhen: { not: { scope: "canvas-explorer" } },
    },
  ],
};

export const FILE_RECORD_FOLDER: ObjectTypeConfig = {
  actions: [
    {
      id: "open",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
    },
    {
      id: "rename",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      disabledWhen: predicates.cannotEdit,
    },
    {
      id: "move",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      disabledWhen: predicates.cannotEdit,
    },
    // Container actions — only where files can be uploaded (files tab folder rows).
    {
      id: "new-folder",
      surfaces: ["left-rail", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.notFileUploadContext,
    },
    {
      id: "upload-files",
      surfaces: ["left-rail", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.notFileUploadContext,
    },
    {
      id: "new-document",
      surfaces: ["left-rail", "context-menu"],
      presentation: "menu-item",
      hiddenWhen: predicates.notFileUploadContext,
    },
    {
      id: "delete",
      surfaces: ["left-rail", "main-view", "context-menu"],
      presentation: "menu-item",
      disabledWhen: predicates.cannotDelete,
    },
  ],
};
